import { z } from 'zod';
import { localDateSchema, syncColumnsSchema } from './common.js';
import {
  athleteGoalSchema,
  availabilitySchema,
  recoveryContextSchema,
  trainingTimeOfDaySchema,
} from './training.js';
import { DEFAULT_MEAL_SLOTS, mealSlotSchema, nutritionGoalSchema } from './nutrition.js';

export const sexSchema = z.enum(['male', 'female']);
export const trainingLevelSchema = z.enum(['beginner', 'intermediate', 'advanced']);

/**
 * Articulaciones en las que se puede declarar una lesión o limitación.
 *
 * NO es `BODY_REGIONS` del dominio: aquellas son regiones musculares para las
 * agujetas, con tres hombros distintos. Una lesión se declara en la
 * articulación. Vive aquí porque se persiste (`profiles.limitations`).
 */
export const jointSchema = z.enum([
  'neck',
  'shoulder',
  'elbow',
  'wrist',
  'lower_back',
  'hip',
  'knee',
  'ankle',
]);

/**
 * `caution`: molesta a veces; se entrena, pero sin lo técnico ni lo pesado en
 * esa zona. `avoid`: no cargarla; se quita todo lo que la carga.
 */
export const limitationLevelSchema = z.enum(['caution', 'avoid']);

export const limitationSchema = z.object({
  joint: jointSchema,
  level: limitationLevelSchema,
});

export const limitationsSchema = z
  .array(limitationSchema)
  .max(8)
  .refine(
    (lista) => new Set(lista.map((l) => l.joint)).size === lista.length,
    'una articulación declarada dos veces',
  );

export type Joint = z.infer<typeof jointSchema>;
export type LimitationLevel = z.infer<typeof limitationLevelSchema>;
export type Limitation = z.infer<typeof limitationSchema>;

export const profileSchema = syncColumnsSchema.extend({
  displayName: z.string().min(1).max(80),
  sex: sexSchema,
  /** Opcional: el perfil inicial no la incluye (ver docs/RISKS.md R-03). */
  birthDate: localDateSchema.nullable(),
  heightCm: z.number().min(120).max(230),
  experienceYears: z.number().min(0).max(80),
  trainingLevel: trainingLevelSchema,
  /** Fecha desde la que el usuario esta inactivo; motiva el bloque de readaptacion. */
  detrainedSince: localDateSchema.nullable(),
  timezone: z.string().min(1).max(64),
  /**
   * `null` = el test inicial no se ha hecho. `.default(null)` y no solo
   * `.nullable()`: una fila anterior a la 0034 no trae la clave (R-16), y sin
   * el valor por defecto dejaría de encajar con su contrato.
   */
  availability: availabilitySchema.nullable().default(null),
  limitations: limitationsSchema.default([]),
});

export type Profile = z.infer<typeof profileSchema>;
export type Sex = z.infer<typeof sexSchema>;
export type TrainingLevel = z.infer<typeof trainingLevelSchema>;

export const notificationPrefsSchema = z.object({
  enabled: z.boolean(),
  workoutReminder: z.boolean(),
  restTimer: z.boolean(),
  weeklyCheckin: z.boolean(),
});

export const dietaryPrefsSchema = z.object({
  cuisines: z.array(z.string().min(1)),
  excluded: z.array(z.string().min(1)),
  allergies: z.array(z.string().min(1)),
  intolerances: z.array(z.string().min(1)),
});

export const learnedPreferenceSchema = z.object({
  key: z.string().min(1),
  value: z.string().min(1),
  evidenceCount: z.number().int().nonnegative(),
  firstSeen: z.iso.datetime({ offset: true }),
  lastSeen: z.iso.datetime({ offset: true }),
  confidence: z.number().min(0).max(1),
});

export const userPreferencesSchema = syncColumnsSchema.extend({
  units: z.object({
    weight: z.literal('kg'),
    distance: z.literal('km'),
  }),
  notificationPrefs: notificationPrefsSchema,
  dietary: dietaryPrefsSchema,
  learnedPreferences: z.array(learnedPreferenceSchema),
  /**
   * Interruptor de «Desactivar IA» (AI_ARCHITECTURE.md §7.3). Apagado, el
   * sistema es 100 % determinista y no sale ni una peticion.
   *
   * `default(true)` no es adorno: la columna se añadio en la migracion 0020 y
   * las filas locales anteriores no la tienen. El `hook('reading')` de
   * `normalizacion.ts` pasa toda fila por este schema al leerla, asi que el
   * valor por defecto se aplica ahi (riesgo R-16).
   */
  aiEnabled: z.boolean().default(true),
  /**
   * Tope de gasto mensual en centimos. Es el ajuste que el usuario cambia; lo
   * que se lleva gastado vive en `ai_usage_budget`, que escribe el servidor.
   *
   * Poner 0 tambien deja el sistema sin IA, pero el interruptor es
   * `aiEnabled`: apagarla no debe costar el presupuesto que habias elegido.
   */
  aiBudgetCents: z.number().int().min(0).max(100_000),
  /**
   * Las comidas que el usuario hace al dia, entre las cinco de
   * `SELECTABLE_MEAL_SLOTS` (requisito §4 del encargo, NUTRITION_ENGINE.md §4.3).
   *
   * **UNA SOLA FUENTE DE VERDAD.** No hay `mealCount` guardado en ninguna
   * parte: el numero de comidas es `mealSlots.length`. Dos campos que tienen
   * que cuadrar entre si acaban sin cuadrar —basta una escritura parcial, un
   * merge de sync o una pantalla que actualice uno— y entonces no hay forma de
   * saber cual de los dos manda. Aqui no hay nada que reconciliar.
   *
   * `default` para las filas viejas: la columna llego en la migracion 0027 y
   * `alter table ... add column ... default` no toca `sync_seq`, asi que una
   * fila local anterior no vuelve a bajar nunca (R-16). El `hook('reading')` de
   * `normalizacion.ts` aplica este defecto al leer, y ademas la migracion
   * fuerza la bajada tocando `updated_at`. Son las dos redes de siempre.
   *
   * El ORDEN no se guarda como dato: se normaliza al de `SELECTABLE_MEAL_SLOTS`
   * (`normalizeMealSlots` en el dominio) antes de escribir. Un array
   * persistido cuyo orden no significa nada es una diferencia que aparece en
   * los conflictos de sync sin querer decir nada.
   */
  mealSlots: z.array(mealSlotSchema).min(1).max(5).default([...DEFAULT_MEAL_SLOTS]),
  /**
   * El objetivo de la alimentacion, ELEGIDO por el usuario (migracion
   * `20260928120000_recuperar_nutricion`).
   *
   * `null` es «todavia no lo ha elegido», y entonces la nutricion no calcula
   * nada y lo pide: no hay un defecto que imponga un deficit o un superavit a
   * quien no lo ha pedido. Por eso el `default` es `null` y no un objetivo: las
   * filas anteriores a la columna (R-16) llegan sin ella y deben leerse igual
   * que una eleccion pendiente, no como una eleccion hecha.
   *
   * Solo lo lee la nutricion. El entrenamiento no mira este campo.
   */
  nutritionGoal: nutritionGoalSchema.nullable().default(null),
  /**
   * Cuando entrena habitualmente. Es el valor por defecto de una sesion, NO la
   * verdad sobre una sesion concreta: eso vive en
   * `training_sessions.training_time_of_day`, que es donde afecta.
   *
   * Existe porque sin el habria que contestar la misma pregunta cuatro veces
   * por semana, y sobre todo porque las sesiones que ya existian no tienen
   * respuesta: con solo el campo por sesion, el reparto peri-entrenamiento se
   * habria quedado sin aplicar en todo lo ya planificado, que es exactamente
   * la trampa del «llega en la Fase N» de CLAUDE.md.
   *
   * Quien resuelve las dos en un valor es `resolveTrainingTimeOfDay`, y es el
   * unico sitio donde se leen.
   */
  defaultTrainingTimeOfDay: trainingTimeOfDaySchema.default('afternoon'),
  /**
   * El OBJETIVO DECLARADO del atleta (§6.1 del encargo, migracion 0029).
   *
   * Se declara, no se deduce. Hasta el 21/09/2026 el motor de bloques tenia una
   * entrada `muscleGainGoalActive` que su UNICO productor pasaba en `false`
   * fijo, y el comentario decia por que: «no hay donde declarar un objetivo de
   * masa. El perfil no tiene campo». Consecuencia medida: la rama de ganancia
   * muscular del arbol de fases era **inalcanzable en la aplicacion**. Una
   * aplicacion de hipertrofia cuyo bloque de hipertrofia no se podia alcanzar.
   *
   * No es un fallo que se viera: el arbol contestaba «mantenimiento», que es
   * una respuesta plausible. Es la trampa de las entradas cegadas que
   * `CLAUDE.md` tiene fichada tres veces, y se cierra dando al dato un sitio
   * donde vivir y una pantalla que lo escriba.
   *
   * `default('muscle_gain')`: es una aplicacion de culturismo natural y quien
   * la instala quiere desarrollar musculo. Un defecto de `maintenance` seria
   * mas «neutro» y dejaria a todo el mundo sosteniendo lo que tiene sin haberlo
   * pedido, que es el mismo error con otro signo. El `default` ademas cubre las
   * filas anteriores a la columna (R-16), junto con la bajada forzada de la
   * migracion.
   */
  trainingGoal: athleteGoalSchema.default('muscle_gain'),
  /**
   * CONTEXTO declarado de recuperacion (§6.2). Lo dice el usuario de si mismo.
   *
   * `reduced` cubre lo que de verdad limita la recuperacion y el sistema no
   * puede medir: un deficit calorico que el usuario lleva por su cuenta, una
   * temporada de dormir mal, estres alto. **Modula el volumen; no elige el
   * bloque.** Esa es la diferencia con el arbol anterior, donde una medida de
   * cintura imponia la fase —y con ella un deficit— sin que nadie lo pidiera.
   *
   * Cadencia no prescribe nutricion. Que el usuario DIGA que esta en deficit es
   * informacion util para no subirle el volumen; deducirlo de una bascula y
   * recetarle una dieta es justo lo que el §6 prohibe.
   */
  recoveryContext: recoveryContextSchema.default('normal'),
});

export type UserPreferences = z.infer<typeof userPreferencesSchema>;
export type LearnedPreference = z.infer<typeof learnedPreferenceSchema>;
